import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { post } from '../../../helpers/api.helper';
import config from '../../../config/config';
import { useVendorStore } from '../../../store/vendorStore';
import { toast } from 'react-toastify';

interface SubscriptionSuccessPageProps {}

export function SubscriptionSuccessPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { vendorId } = useVendorStore();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('구독 결제를 처리하고 있습니다...');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const processSubscription = async () => {
            try {
                // URL 파라미터에서 필요한 정보 추출
                const resultCode = searchParams.get('ResultCode');
                const resultMsg = searchParams.get('ResultMsg');
                const bid = searchParams.get('BID');
                const userId = searchParams.get('userId');
                const subscriptionPlanId = searchParams.get('subscriptionPlanId');
                const businessCode = searchParams.get('businessCode');

                // 필수 파라미터 확인
                if (!userId) {
                    throw new Error('사용자 정보를 찾을 수 없습니다.');
                }

                // ResultCode가 F100이 아니면 에러 처리
                if (resultCode !== 'F100') {
                    throw new Error(resultMsg || '결제 처리에 실패했습니다.');
                }

                // subscriptionPlanId가 없거나 'undefined', 'null' 문자열인 경우 카드 등록만
                if (!subscriptionPlanId || subscriptionPlanId === 'undefined' || subscriptionPlanId === 'null') {
                    setStatus('success');
                    setMessage('카드가 성공적으로 등록되었습니다!');

                    // 성공 토스트 메시지 표시
                    toast.success('카드가 성공적으로 등록되었습니다!', {
                        position: 'top-center',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });

                    // 2초 후 계정 설정 페이지로 이동
                    setTimeout(() => {
                        navigate('/account/settings#billing');
                    }, 2000);
                    return;
                }

                // subscriptionPlanId가 있는 경우에만 approve API 호출
                const approvePayload = {
                    subscriptionPlanId,
                    userId,
                    bid,
                    businessCode,
                };

                // approve API 호출
                const response = await post(`${config.api.matching_api}/billing/approve`, approvePayload);

                const result = response.data;
                if (result && result.ResultCode === '3001') {
                    setStatus('success');
                    setMessage('구독이 성공적으로 완료되었습니다!');

                    // 성공 토스트 메시지 표시
                    toast.success('구독이 성공적으로 완료되었습니다!', {
                        position: 'top-center',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });

                    // 2초 후 계정 설정 페이지로 이동
                    setTimeout(() => {
                        navigate('/account/settings#billing');
                    }, 2000);
                } else {
                    const errorMsg = result?.ResultMsg || '구독 결제 처리에 실패했습니다.';
                    throw new Error(errorMsg);
                }
            } catch (error) {
                console.error('Subscription processing failed:', error);
                setStatus('error');
                setError(error instanceof Error ? error.message : '구독 처리 중 오류가 발생했습니다.');
            }
        };

        processSubscription();
    }, [searchParams, navigate]);

    const handleRetry = () => {
        setStatus('loading');
        setError(null);
        setMessage('구독 결제를 다시 처리하고 있습니다...');
        // 페이지 새로고침으로 재시도
        window.location.reload();
    };

    const handleGoToBilling = () => {
        navigate('/account/settings#billing');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                backgroundColor: '#f8f9fa',
            }}
        >
            <Box
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: 2,
                    padding: 4,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                }}
            >
                {status === 'loading' && (
                    <>
                        <CircularProgress
                            size={60}
                            sx={{
                                color: '#FF4B6B',
                                marginBottom: 3,
                            }}
                        />
                        <Typography variant="h5" sx={{ marginBottom: 2, fontWeight: 600 }}>
                            {message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            잠시만 기다려주세요...
                        </Typography>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: '#10B981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px auto',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                }}
                            >
                                ✓
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ marginBottom: 2, fontWeight: 600, color: '#10B981' }}>
                            {message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 3 }}>
                            계정 설정 페이지로 이동합니다...
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleGoToBilling}
                            sx={{
                                backgroundColor: '#FF4B6B',
                                color: 'white',
                                '&:hover': { backgroundColor: '#E03E5A' },
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                borderRadius: '8px',
                            }}
                        >
                            계정 설정으로 이동
                        </Button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: '#EF4444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px auto',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                }}
                            >
                                ✗
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ marginBottom: 2, fontWeight: 600, color: '#EF4444' }}>
                            처리 실패
                        </Typography>
                        <Alert severity="error" sx={{ marginBottom: 3, textAlign: 'left' }}>
                            {error}
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={handleRetry}
                                sx={{
                                    borderColor: '#FF4B6B',
                                    color: '#FF4B6B',
                                    '&:hover': {
                                        borderColor: '#E03E5A',
                                        backgroundColor: '#FFF5F5',
                                    },
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                }}
                            >
                                다시 시도
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleGoToBilling}
                                sx={{
                                    backgroundColor: '#FF4B6B',
                                    color: 'white',
                                    '&:hover': { backgroundColor: '#E03E5A' },
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                }}
                            >
                                계정 설정으로 이동
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}

export default SubscriptionSuccessPage;
